import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleIcon from "@mui/icons-material/People";
import QuizIcon from "@mui/icons-material/Quiz";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getUserInfoById } from "../../services/UserService";

const drawerWidth = 280;
const collapsedDrawerWidth = 64;

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await getUserInfoById();
      if (response && response.data) {
        setUserEmail(response.data.email || "");
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("jwt");
    localStorage.removeItem("user");
    navigate("/login");
    handleMenuClose();
  };

  const menuItems = [
    {
      text: "Quản lý User",
      icon: <PeopleIcon />,
      path: "/admin/users",
    },
    {
      text: "Ngân hàng câu hỏi",
      icon: <QuizIcon />,
      path: "/admin/questions",
    },
  ];

  const drawer = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: "#1976d2",
        color: "white",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 16px",
          minHeight: "64px",
        }}
      >
        {desktopOpen && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              color: "white",
              display: { xs: "none", sm: "block" },
            }}
          >
            Iluvmath Admin
          </Typography>
        )}
        <IconButton
          onClick={handleDesktopDrawerToggle}
          sx={{
            display: { xs: "none", sm: "flex" },
            color: "white",
            marginLeft: desktopOpen ? "auto" : "0",
          }}
        >
          {desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            display: { xs: "block", sm: "none" },
            color: "white",
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.2)" }} />
      <List sx={{ padding: "16px 8px", flex: 1 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: "8px",
                  marginBottom: "8px",
                  color: "white",
                  backgroundColor: isActive
                    ? "rgba(255, 255, 255, 0.2)"
                    : "transparent",
                  fontWeight: isActive ? 600 : 400,
                  justifyContent: desktopOpen ? "flex-start" : "center",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                  },
                }}
                title={!desktopOpen ? item.text : ""}
              >
                <ListItemIcon
                  sx={{
                    color: "white",
                    minWidth: desktopOpen ? "40px" : "0",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {desktopOpen && <ListItemText primary={item.text} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  const currentDrawerWidth = desktopOpen ? drawerWidth : collapsedDrawerWidth;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          backgroundColor: "#fff",
          color: "#333",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          transition: "width 0.3s, margin-left 0.3s",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Trang quản trị
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {userEmail && (
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  display: { xs: "none", sm: "block" },
                }}
              >
                {userEmail}
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="large"
                onClick={handleMenuClick}
                sx={{ color: "#1976d2" }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: "#1976d2" }}>
                  <AccountCircleIcon />
                </Avatar>
              </IconButton>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              {userEmail && (
                <MenuItem disabled>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {userEmail}
                  </Typography>
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Đăng xuất</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: currentDrawerWidth },
          flexShrink: { sm: 0 },
          transition: "width 0.3s",
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: currentDrawerWidth,
              transition: "width 0.3s",
              overflowX: "hidden",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          backgroundColor: "#f5f7fa",
          minHeight: "100vh",
          transition: "width 0.3s",
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
